import { postChatContext } from 'features/dashboard/api/chatApi';
import { capDashboardAttachments } from 'features/dashboard/constants/dashboardContextLimits';
import { trackClientEvent } from 'shared/utils/trodoEvents';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    createChatSession,
    getChatMessages,
    listChatSessions,
    postChatMessage,
} from 'shared/services/chatService';
import {
    invalidateChatCache,
    setCachedMessages,
    setCachedSessionsList,
} from 'shared/utils/chatCache';
import usePolling, { onTabReturn } from 'shared/hooks/usePolling';

function readUserId() {
  try {
    const raw = localStorage.getItem('User');
    if (!raw) return null;
    const u = JSON.parse(raw);
    if (u.id != null) return String(u.id);
    return null;
  } catch {
    return null;
  }
}

/** Count dashboard @-tags on a message (API may use camelCase or snake_case). */
function dashboardAttachmentCount(msg) {
  const m = msg?.metadata;
  if (!m || typeof m !== 'object') return 0;
  const a = m.dashboardAttachments ?? m.dashboard_attachments;
  return Array.isArray(a) ? a.length : 0;
}

/** Short summary of tool params for planner UI (funnel steps, presets, etc.). */
function summarizePlannerToolParams(params) {
  if (params == null || typeof params !== 'object') return '';
  const p = params;
  const parts = [];
  if (Array.isArray(p.steps) && p.steps.length) {
    const chain = p.steps
      .map((s) => (s && (s.eventName || s.event)) || '?')
      .join(' → ');
    parts.push(`steps: ${chain}`);
  }
  const tr = p.time_range;
  if (tr && typeof tr === 'object' && tr.preset) {
    parts.push(`preset: ${tr.preset}`);
  } else if (tr && typeof tr === 'object' && (tr.start || tr.end)) {
    parts.push(`range: ${[tr.start, tr.end].filter(Boolean).join('–')}`);
  }
  if (parts.length) return parts.join(' · ');
  const keys = Object.keys(p);
  if (!keys.length) return '';
  return keys.slice(0, 4).join(', ') + (keys.length > 4 ? '…' : '');
}

/** True when the plan has no tool calls to run (typical for “ask user first” flows). */
function getChatApiBaseUrl() {
  const explicitApiUrl = (process.env.REACT_APP_API_SERVER_URL || '').trim();
  return explicitApiUrl ? `${explicitApiUrl}/api` : '/api';
}

function buildInvestigateFetchHeaders(teamId) {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (teamId != null && teamId !== '') headers['X-Team-Id'] = String(teamId);
  return headers;
}

/**
 * Reconstruct the `investigateStream` shape from a persisted trail
 * (metadata.events) so reopening a session replays the same thinking /
 * tool result / reasoning / answer sequence the user saw the first time.
 *
 * Designed to be deterministic — no LLM, no network. Just walks events in
 * order and builds the final UI state.
 */
/**
 * One tool call as the trail shows it: the human label, the raw name, and the
 * model's own purpose for it (`detail`, e.g. "failed runs by agent · last 7
 * days") — the line the trail leads with, rather than the tool's name.
 */
function callFrom(d) {
  return {
    tool: typeof d?.tool === 'string' ? d.tool.trim() : '',
    name: typeof d?.name === 'string' ? d.name : '',
    detail: typeof d?.detail === 'string' ? d.detail : '',
    wave: typeof d?.wave === 'number' ? d.wave : null,
  };
}

function rebuildInvestigateStreamFromEvents(events, message) {
  if (!Array.isArray(events) || events.length === 0) return null;
  const stream = {
    thinking: false,
    thinkingLabel: '',
    tools: [],
    toolResults: [],
    reasoning: [],
    steps: [],
    plan: null,
    resultKind: null,
    body: '',
    streaming: false,
    replayed: true,
    calls: [],
    durationMs: null,
  };
  for (const e of events) {
    if (!e || typeof e !== 'object') continue;
    // Each event carries ms since the run started: the last one is how long it worked.
    if (typeof e.t === 'number' && e.t > (stream.durationMs || 0)) stream.durationMs = e.t;
    const ev = e.event;
    const d = e.data || {};
    if (ev === 'thinking') {
      stream.thinkingLabel =
        typeof d.message === 'string' && d.message.trim() ? d.message.trim() : stream.thinkingLabel;
    } else if (ev === 'step') {
      // v2 structured step log — upsert by (phase, id) so status transitions
      // (pending → running → ok) update in place, appended in arrival order.
      if (d && typeof d === 'object' && d.phase) {
        if (d.phase === 'plan' && d.plan) stream.plan = d.plan;
        const key = `${d.phase}:${d.id || ''}`;
        const idx = stream.steps.findIndex((s) => `${s.phase}:${s.id || ''}` === key);
        if (idx >= 0) stream.steps[idx] = { ...stream.steps[idx], ...d };
        else stream.steps.push({ ...d });
      }
    } else if (ev === 'calling_tool') {
      const name = typeof d.tool === 'string' ? d.tool.trim() : '';
      if (name && !stream.tools.includes(name)) stream.tools.push(name);
      if (name) stream.calls.push(callFrom(d));
    } else if (ev === 'tool_result') {
      stream.toolResults.push({
        cycle: typeof d.cycle === 'number' ? d.cycle : null,
        tool: typeof d.tool === 'string' ? d.tool : '',
        // What the call was and what came back — dropped before L5, so a
        // replayed trail must carry exactly what the live one showed.
        name: typeof d.name === 'string' ? d.name : '',
        detail: typeof d.detail === 'string' ? d.detail : '',
        rows: typeof d.rows === 'number' ? d.rows : null,
        dataset: typeof d.dataset === 'string' ? d.dataset : '',
        wave: typeof d.wave === 'number' ? d.wave : null,
        status: typeof d.status === 'string' ? d.status : 'unknown',
        summary: typeof d.summary === 'string' ? d.summary : '',
        error: typeof d.error === 'string' ? d.error : '',
        chat_render: d.chat_render && typeof d.chat_render === 'object' ? d.chat_render : null,
      });
    } else if (ev === 'reasoning') {
      const reason = typeof d.reason === 'string' ? d.reason.trim() : '';
      if (reason) {
        stream.reasoning.push({
          cycle: typeof d.cycle === 'number' ? d.cycle : null,
          reason,
          confidence: typeof d.confidence === 'number' ? d.confidence : null,
          sufficient: !!d.sufficient,
        });
      }
    } else if (ev === 'answer_stream' || ev === 'delta') {
      // Coalesced delta buffer captured on the server.
      const text =
        typeof d.text === 'string'
          ? d.text
          : typeof d === 'string'
            ? d
            : '';
      if (text) stream.body += text;
    } else if (ev === 'answer') {
      stream.resultKind = 'answer';
      if (typeof d.finalAnswer === 'string' && d.finalAnswer.trim()) {
        stream.body = d.finalAnswer; // trust final over accumulated deltas
      }
    } else if (ev === 'clarify') {
      stream.resultKind = 'clarify';
      if (typeof d.question === 'string') stream.body = d.question;
      if (Array.isArray(d.options)) stream.clarifyOptions = d.options;
      if (Array.isArray(d.questions) && d.questions.length) stream.clarifyQuestions = d.questions;
      if (d.clarificationId) stream.clarificationId = String(d.clarificationId);
    } else if (ev === 'error') {
      stream.resultKind = 'error';
      if (typeof d.message === 'string') stream.body = d.message;
    }
  }
  // If we never captured a final body but the message itself has content,
  // fall back to the persisted content (covers older messages with no trail).
  if (!stream.body && message && typeof message.content === 'string') {
    stream.body = message.content;
    if (!stream.resultKind) stream.resultKind = 'answer';
  }
  if (!stream.resultKind) stream.resultKind = 'answer';
  return stream;
}

/** For each assistant message, attach an `investigateStream` derived from
 *  its persisted metadata.events. Idempotent. Preserves any in-flight
 *  stream object already present (don't clobber a live row mid-stream). */
/**
 * How a run ended without an answer, from what the server stored on its
 * message: a stopped run, no credit, a paused or failed one. null = an answer.
 */
export function endReasonFor(md) {
  if (!md) return null;
  if (md.ask_cancelled) return 'cancelled';
  if (md.ask_no_credit) return 'no_credit';
  if (md.ask_paused) return 'paused';
  if (md.ask_failed) return 'failed';
  return null;
}

export function attachReplayedStreams(messages) {
  if (!Array.isArray(messages)) return messages;
  return messages
    // A gate card two writers both delivered. The duplicate is demoted rather
    // than deleted (migration 20260902_ask_analyst_role.sql), so the history
    // keeps it and the thread does not show the same plan twice.
    .filter((m) => !(m && m.metadata && m.metadata.ask_plan_gate_superseded))
    // ONE QUESTION PER RUN. A deep run's clarify question has two writers —
    // the bridge watching the run and the research worker — and each checks
    // the other has not written first. Measured: they wrote within the same
    // second, neither saw the other's uncommitted row, and the reader got the
    // same question twice above its card. The latest copy is the one kept
    // (its clarificationId is the freshest registration).
    .filter((m, i, all) => {
      const md = m && m.metadata;
      if (!md || md.kind !== 'clarify' || !md.ask_run_id) return true;
      return !all.slice(i + 1).some((n) => n?.metadata?.kind === 'clarify'
        && n.metadata.ask_run_id === md.ask_run_id && n.content === m.content);
    })
    .map((m) => {
    if (!m || m.role !== 'assistant') return m;
    if (m.investigateStream && m.investigateStream.streaming) return m;
    const events = m.metadata && Array.isArray(m.metadata.events) ? m.metadata.events : null;
    const answerDoc =
      m.metadata && m.metadata.answer && typeof m.metadata.answer === 'object' ? m.metadata.answer : null;
    let inv = null;
    if (events && events.length > 0) {
      inv = rebuildInvestigateStreamFromEvents(events, m);
      if (inv && answerDoc) inv.answer = answerDoc;
    } else if (m.metadata && m.metadata.kind === 'clarify' && Array.isArray(m.metadata.clarify_questions)) {
      // A clarify the deep worker posted has no trail (its stream had ended):
      // it is still a clarify card, answerable through its clarificationId.
      const md = m.metadata;
      inv = {
        thinking: false,
        thinkingLabel: '',
        tools: [],
        toolResults: [],
        reasoning: [],
        resultKind: 'clarify',
        body: typeof m.content === 'string' ? m.content : '',
        ...(md.clarify_questions.length ? { clarifyQuestions: md.clarify_questions } : {}),
        ...(md.clarificationId ? { clarificationId: String(md.clarificationId) } : {}),
        streaming: false,
        replayed: true,
      };
    } else if (m.content && typeof m.content === 'string') {
      // Legacy messages with no trail: render them as plain answer rows.
      inv = {
        thinking: false,
        thinkingLabel: '',
        tools: [],
        toolResults: [],
        reasoning: [],
        resultKind: 'answer',
        body: m.content,
        streaming: false,
        replayed: true,
        ...(answerDoc ? { answer: answerDoc } : {}),
      };
    }
    const ended = endReasonFor(m.metadata);
    if (inv && ended) {
      inv.resultKind = 'ended';
      inv.endReason = ended;
    }
    return inv ? { ...m, investigateStream: inv } : m;
  });
}

/** Parse one SSE block (between blank lines). */
function parseSseEventBlock(block) {
  let eventName = 'message';
  const dataLines = [];
  const lines = block.split(/\n/);
  for (const line of lines) {
    if (line === '' || line.startsWith(':')) continue;
    const colon = line.indexOf(':');
    const field = colon >= 0 ? line.slice(0, colon) : line;
    let value = colon >= 0 ? line.slice(colon + 1) : '';
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'event') eventName = value.trim();
    else if (field === 'data') dataLines.push(value);
  }
  const dataStr = dataLines.join('\n');
  return { eventName, dataStr };
}

/**
 * Read fetch SSE body and invoke onEvent(eventName, parsedJson) for each event.
 * Returns when the stream ends or after optional terminal events (answer/clarify) if reader is cancelled by caller.
 */
async function consumeInvestigateSse(response, onEvent) {
  const reader = response.body && response.body.getReader ? response.body.getReader() : null;
  if (!reader) return;
  const decoder = new TextDecoder();
  let buffer = '';
  let readerClosed = false;

  const processCompleteBlocks = () => {
    const parts = buffer.split(/\r?\n\r?\n/);
    buffer = parts.pop() || '';
    for (const block of parts) {
      if (!block.trim()) continue;
      const { eventName, dataStr } = parseSseEventBlock(block);
      if (!dataStr) continue;
      try {
        const data = JSON.parse(dataStr);
        if (onEvent(eventName, data) === true) {
          readerClosed = true;
          reader.cancel().catch(() => {});
          return;
        }
      } catch {
        /* ignore non-JSON */
      }
    }
  };

  try {
    while (!readerClosed) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      processCompleteBlocks();
    }
    if (!readerClosed && buffer.trim()) {
      const tail = buffer.trim();
      const { eventName, dataStr } = parseSseEventBlock(tail);
      if (dataStr) {
        try {
          const data = JSON.parse(dataStr);
          onEvent(eventName, data);
        } catch {
          /* ignore */
        }
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* ignore */
    }
  }
}

/**
 * Chat state: team from context, user id for cache keys only (JWT authorizes API).
 * Syncs ?session= with active thread; loads sessions + messages from API.
 */
export function useChatSession(teamId, teamLoading, options = {}) {
  const {
    skipInitialHydration: skipInitialHydrationOption = false,
    homePendingMessage: homePendingOpt = null,
    /**
     * Whether the active session id lives in `?session=`.
     *
     * True on the chat page, where the URL is the thing you copy to share a
     * conversation. False in the side panel, which opens on top of Traces,
     * Runs, Issues — pages that own their query string and rebuild it whole on
     * every filter change. A session id written there would be dropped the
     * moment someone touched a filter, detaching the chat from its own
     * history mid-conversation.
     */
    syncUrl = true,
  } = options;
  const [urlParams, setUrlParams] = useSearchParams();
  const [localSessionId, setLocalSessionId] = useState(null);

  // One shape for both worlds: callers keep calling setSearchParams({session})
  // or setSearchParams({}) and do not care where it lands.
  const setSearchParams = useCallback((next, opts) => {
    if (syncUrl) { setUrlParams(next, opts); return; }
    const sid = (next && typeof next === 'object' && !Array.isArray(next)) ? (next.session ?? null) : null;
    setLocalSessionId(sid ? String(sid) : null);
  }, [syncUrl, setUrlParams]);

  const urlSessionParam = useMemo(() => urlParams.get('session'), [urlParams]);
  const sessionParam = syncUrl ? urlSessionParam : localSessionId;

  /** Team home composer: navigate(..., { state: { pendingHomeMessage } }) — ChatPage runs newChat + sendMessage */
  const homeBootstrapMessage = useMemo(() => {
    if (typeof homePendingOpt !== 'string') return null;
    const t = homePendingOpt.trim();
    return t === '' ? null : t;
  }, [homePendingOpt]);

  const skipInitialHydration = Boolean(skipInitialHydrationOption || homeBootstrapMessage);

  const userId = readUserId();

  const [sessions, setSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [messagesNextCursor, setMessagesNextCursor] = useState(null);
  const [messagesHasMore, setMessagesHasMore] = useState(false);

  const teamIdRef = useRef(null);

  // When sendMessage sets ?session= itself (draft thread → first send), the
  // sessionParam effect below is skipped — otherwise it would race the SSE
  // stream and call loadMessagesForSession(sid), wiping the streaming row.
  const ownUrlUpdateRef = useRef(null);
  // True while an SSE stream is live; focus/visibility refetches suppress
  // reloads so they don't overwrite the in-flight stream.
  const streamingRef = useRef(false);

  useEffect(() => {
    if (teamIdRef.current != null && teamIdRef.current !== teamId) {
      setSessions([]);
      setMessages([]);
      setActiveSessionId(null);
      setMessagesNextCursor(null);
      setMessagesHasMore(false);
      if (userId) invalidateChatCache(userId, teamIdRef.current);
    }
    teamIdRef.current = teamId ?? null;
  }, [teamId, userId]);

  // The last list the server returned. `messages` is state and cannot be read
  // back synchronously after a reload, and the recovery path below has to know
  // whether an answer actually landed before it claims one was lost.
  const loadedRef = useRef([]);
  // When the last live answer arrived. The thread reloads from the server the
  // moment it does, and the saved copy replaced the live row before it had
  // typed a word; the copy carries this, so the reveal runs on across the swap.
  const revealRef = useRef(0);
  const REVEAL_CARRY_MS = 4000;

  const loadMessagesForSession = useCallback(
    async (sessionId, cursor = null, opts = {}) => {
      const { silent = false } = opts;
      if (!teamId || !sessionId) return;
      if (!silent) setMessagesLoading(true);
      setError('');
      try {
        const data = await getChatMessages(sessionId, teamId, {
          limit: 50,
          ...(cursor ? { cursor } : {}),
        });
        const list = attachReplayedStreams(data.messages || []);
        loadedRef.current = list;   // what the server actually holds, for callers that must judge
        if (cursor) {
          setMessages((prev) => [...list, ...prev]);
        } else {
          // Preserve _clientKey + dashboard tags if GET omits metadata (race / older API)
          setMessages((prev) => {
            const prevById = new Map(prev.map((m) => [String(m.id), m]));
            const next = list.map((srv) => {
              const p = prevById.get(String(srv.id));
              let merged = srv;
              if (p && p._clientKey) {
                merged = { ...merged, _clientKey: p._clientKey };
              }
              const pTags = dashboardAttachmentCount(p);
              const srvTags = dashboardAttachmentCount(merged);
              if (p && pTags > 0 && srvTags === 0 && p.metadata) {
                merged = { ...merged, metadata: p.metadata };
              }
              // Preserve a live in-flight stream — don't clobber with replay.
              if (p && p.investigateStream && p.investigateStream.streaming) {
                merged = { ...merged, investigateStream: p.investigateStream };
              }
              return merged;
            });
            // The answer that just arrived keeps typing out in its saved copy.
            const at = revealRef.current;
            if (!at || Date.now() - at > REVEAL_CARRY_MS) return next;
            let last = -1;
            next.forEach((m, i) => { if (m && m.role === 'assistant') last = i; });
            if (last < 0) return next;
            const m = next[last];
            next[last] = { ...m, revealAt: at, ...(m.investigateStream ? { investigateStream: { ...m.investigateStream, revealAt: at } } : {}) };
            return next;
          });
        }
        setMessagesNextCursor(data.nextCursor || null);
        setMessagesHasMore(Boolean(data.hasMore));
        if (userId) {
          setCachedMessages(userId, teamId, sessionId, {
            messages: cursor ? [...list] : list,
            nextCursor: data.nextCursor,
            hasMore: data.hasMore,
          });
        }
        // Returned so a caller waiting for a particular message (a deep run's
        // answer) can tell whether it has landed yet.
        return list;
      } catch (err) {
        const status = err.response?.status;
        const msg = err.response?.data?.message || err.message || 'Failed to load messages';
        setError(msg);
        if (status === 403 || status === 404) {
          setSearchParams({}, { replace: true });
          setActiveSessionId(null);
          setMessages([]);
        }
        throw err;
      } finally {
        if (!silent) setMessagesLoading(false);
      }
    },
    [teamId, userId, setSearchParams]
  );

  /**
   * Load session list and align with ?session= (refresh-safe).
   * Pattern: URL session → load; else latest session → set URL; else empty.
   */
  useEffect(() => {
    if (teamLoading || !teamId) return;

    let cancelled = false;

    (async () => {
      setSessionsLoading(true);
      setError('');
      try {
        const data = await listChatSessions(teamId);
        if (cancelled) return;
        const list = data.sessions || [];
        setSessions(list);
        if (userId) setCachedSessionsList(userId, teamId, list);

        if (skipInitialHydration) {
          setActiveSessionId(null);
          setMessages([]);
          setMessagesNextCursor(null);
          setMessagesHasMore(false);
          setSearchParams(
            (prev) => {
              const n = new URLSearchParams(prev);
              n.delete('session');
              return n;
            },
            { replace: true }
          );
          return;
        }

        if (sessionParam) {
          // If sendMessage just set ?session= itself, skip the reload — the
          // in-flight SSE stream owns `messages` until it finishes.
          if (ownUrlUpdateRef.current === sessionParam) {
            ownUrlUpdateRef.current = null;
            setActiveSessionId(sessionParam);
            return;
          }
          setActiveSessionId(sessionParam);
          try {
            await loadMessagesForSession(sessionParam);
          } catch {
            /* loadMessagesForSession clears URL on 403/404 */
          }
          return;
        }

        // No ?session=: start fresh — do not auto-open the latest thread (avoids
        // "last chat" every time you return to this page without a URL).
        setActiveSessionId(null);
        setMessages([]);
        setMessagesNextCursor(null);
        setMessagesHasMore(false);
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.message || err.message || 'Failed to load chat');
      } finally {
        if (!cancelled) setSessionsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    teamId,
    teamLoading,
    sessionParam,
    skipInitialHydration,
    loadMessagesForSession,
    setSearchParams,
    userId,
  ]);

  const selectSession = useCallback(
    async (sessionId) => {
      const sid = String(sessionId);
      setActiveSessionId(sid);
      setSearchParams({ session: sid }, { replace: true });
      await loadMessagesForSession(sid);
    },
    [loadMessagesForSession, setSearchParams]
  );

  // Refetch the active session when the person comes back to the tab, so a
  // reply that landed while they were away (a run finishing on the server, a
  // teammate in the same thread) shows without a refresh. Only after a real
  // absence: this used to reload on every window focus with a 1 s guard, and a
  // browser whose focus flickered every 6 s re-fetched one thread ~6,000 times
  // in ten hours (26 Oct).
  useEffect(() => {
    if (!activeSessionId) return undefined;
    return onTabReturn(() => {
      if (streamingRef.current) return;
      loadMessagesForSession(activeSessionId, null, { silent: true }).catch(() => {});
    }, { minAway: 30_000, minGap: 30_000 });
  }, [activeSessionId, loadMessagesForSession]);

  // A conversation opened while its answer is still being written: the panel's
  // "open in full" lands here mid-run, and so does a plain reload. The run is
  // durable on the server, so the page shows the working state and asks for
  // the thread again every few seconds until the reply is there. Bounded to a
  // recent question so a run that died long ago is not waited on forever.
  const RESUME_WINDOW_MS = 10 * 60 * 1000;
  const [awaitingReply, setAwaitingReply] = useState(false);
  useEffect(() => {
    const last = messages[messages.length - 1];
    const createdAt = last ? new Date(last.createdAt || last.created_at || 0).getTime() : 0;
    const open = Boolean(
      activeSessionId
        && last
        && last.role === 'user'
        && !last.pending
        && !last.optimistic
        && !last.failed
        && !sending
        && !streamingRef.current
        && Date.now() - createdAt < RESUME_WINDOW_MS
    );
    setAwaitingReply(open);
  }, [messages, activeSessionId, sending, RESUME_WINDOW_MS]);
  // 3s, backing off to 10s on a long answer, nothing while the tab is hidden.
  // Coming back to the tab is the focus effect above's job, not this one's.
  usePolling(() => {
    if (streamingRef.current) return undefined;
    return loadMessagesForSession(activeSessionId, null, { silent: true }).catch(() => {});
  }, { enabled: awaitingReply, interval: 3000, maxInterval: 10000, immediate: false, refreshOnFocus: false, key: activeSessionId });

  /** Clears the composer to a draft thread. No server session until the first send (see sendMessage). */
  const newChat = useCallback(async () => {
    if (!teamId) return;
    setError('');
    if (userId) invalidateChatCache(userId, teamId);
    setActiveSessionId(null);
    setMessages([]);
    setMessagesNextCursor(null);
    setMessagesHasMore(false);
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        n.delete('session');
        return n;
      },
      { replace: true }
    );
    try {
      const data = await listChatSessions(teamId);
      setSessions(data.sessions || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not load chats');
    }
  }, [teamId, userId, setSearchParams]);

  const loadOlderMessages = useCallback(async () => {
    if (!activeSessionId || !messagesNextCursor || messagesLoading) return;
    await loadMessagesForSession(activeSessionId, messagesNextCursor);
  }, [activeSessionId, messagesNextCursor, messagesLoading, loadMessagesForSession]);

  const sendMessage = useCallback(
    async (text, options = {}) => {
      // options.clarify carries { clarificationId, answers } when this send is
      // an ANSWER to a question the analyst already asked. It rides sendMessage
      // so the answer reuses the same streaming plumbing, and the backend
      // resumes the run that asked instead of opening a new one.
      // options.mode / options.effort: the composer's Analyze control —
      // 'deep' + an effort level when Extended thinking is on.
      const { attachments = [], maxIterations, clarify, mode, effort, screenContext } = options;
      const trimmed = String(text || '').trim();
      const defaultMsg = 'Analyze the attached dashboard context.';
      if ((!trimmed && attachments.length === 0) || !teamId || sending) return;

      const displayText = trimmed || defaultMsg;

      const cappedAttachments = capDashboardAttachments(attachments);

      const payloadAttachments = [];
      const dashboardAttachmentsMeta = [];
      const seen = new Set();
      for (const a of cappedAttachments) {
        if (!a || (a.kind !== 'board' && a.kind !== 'card')) continue;
        const bid = Number(a.boardId);
        if (!bid || Number.isNaN(bid)) continue;
        const key =
          a.kind === 'card'
            ? `c:${bid}:${Number(a.cardId)}`
            : `b:${bid}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const label = typeof a.label === 'string' ? a.label.slice(0, 200) : '';
        if (a.kind === 'card') {
          const cid = Number(a.cardId);
          if (!cid || Number.isNaN(cid)) continue;
          payloadAttachments.push({ kind: 'card', boardId: bid, cardId: cid });
          dashboardAttachmentsMeta.push({ kind: 'card', boardId: bid, cardId: cid, label });
        } else {
          payloadAttachments.push({ kind: 'board', boardId: bid });
          dashboardAttachmentsMeta.push({ kind: 'board', boardId: bid, label });
        }
      }

      // Recorded on the message so history shows what Ask was looking at when
      // the question was asked — the answer is only reproducible with it.
      const userMessageMetadata =
        (dashboardAttachmentsMeta.length > 0 || screenContext)
          ? {
            ...(dashboardAttachmentsMeta.length > 0 ? { dashboardAttachments: dashboardAttachmentsMeta } : {}),
            ...(screenContext ? { screenContext } : {}),
          }
          : undefined;

      trackClientEvent(
        'chat_message_sent',
        {
          message_length: displayText.length,
          attachment_count: payloadAttachments.length,
          has_attachments: payloadAttachments.length > 0,
          session_existed: !!activeSessionId,
        },
        { category: 'product' }
      );

      // Stable key for React list + merge: survives id change temp → server without remount flicker
      const clientKey = `opt_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
      setSending(true);
      setError('');
      setMessages((m) => [
        ...m,
        {
          _clientKey: clientKey,
          id: clientKey,
          role: 'user',
          content: displayText,
          ...(userMessageMetadata ? { metadata: userMessageMetadata } : {}),
          pending: true,
          optimistic: true,
        },
      ]);

      const startedWithoutSession = !activeSessionId;

      try {
        let sid = activeSessionId;
        if (!sid) {
          const { session } = await createChatSession(teamId);
          sid = String(session.id);
          setActiveSessionId(sid);
          const listData = await listChatSessions(teamId);
          setSessions(listData.sessions || []);
        }

        const { message: saved, session: updatedSession } = await postChatMessage(sid, teamId, {
          content: displayText,
          role: 'user',
          ...(userMessageMetadata
            ? {
                metadata: userMessageMetadata,
                dashboardAttachments: userMessageMetadata.dashboardAttachments,
              }
            : {}),
        });

        if (userId) invalidateChatCache(userId, teamId, sid);

        // In-place merge: keep dashboard tags if POST response omits metadata
        setMessages((m) =>
          m.map((row) => {
            if (!(row._clientKey === clientKey && saved)) return row;
            const srvTags = dashboardAttachmentCount(saved);
            const localTags = dashboardAttachmentCount(row);
            const metadata =
              srvTags > 0 ? saved.metadata : localTags > 0 ? row.metadata : saved.metadata ?? row.metadata;
            return {
              ...saved,
              metadata,
              _clientKey: clientKey,
              pending: false,
              optimistic: false,
              failed: false,
            };
          })
        );

        // Update URL only after the first message exists on the server — avoids the ?session= effect
        // firing GET /messages before POST and replacing state with [] (main flicker cause).
        if (startedWithoutSession) {
          ownUrlUpdateRef.current = sid;
          setSearchParams({ session: sid }, { replace: true });
        }

        let listData = await listChatSessions(teamId);
        setSessions(listData.sessions || []);
        if (updatedSession) {
          setSessions((prev) =>
            prev.map((s) => (String(s.id) === String(updatedSession.id) ? { ...s, ...updatedSession } : s))
          );
        }

        // Layers 2–4: investigate (planner + orchestrator + MECE tools). Clarify / plan / full execution.
        let dashboardExecution = null;
        try {
          const investigateUrl = `${getChatApiBaseUrl()}/chat/investigate`;
          // "Today" means today where the person is; the server has no other way to know.
          let timezone;
          try { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { timezone = undefined; }
          const investigatePayload = {
            teamId: Number(teamId),
            query: displayText,
            sessionId: sid,
            ...(mode ? { mode } : {}),
            ...(effort ? { effort } : {}),
            ...(timezone ? { timezone } : {}),
            ...(payloadAttachments.length > 0 ? { attachments: payloadAttachments } : {}),
            ...(screenContext ? { screenContext } : {}),
            ...(clarify && clarify.clarificationId
              ? { clarificationId: String(clarify.clarificationId), answers: clarify.answers || {} }
              : {}),
            ...(Number.isFinite(maxIterations) && maxIterations >= 1
              ? { maxIterations: Math.min(5, Math.floor(maxIterations)) }
              : {}),
          };
          const isDeepRequest = mode === 'deep';
          const abortCtrl = new AbortController();
          // Idle timeout, NOT a duration cap. The server heartbeats every ~20s
          // for as long as the run lives, so two quiet minutes means the
          // stream is genuinely dead. The previous 5-minute wall-clock abort
          // killed healthy deep-research streams from the client side and
          // surfaced "BodyStreamBuffer was aborted" — the client cancelling
          // its own request and then reporting it as a failure.
          const IDLE_MS = 2 * 60 * 1000;
          let abortTimer = setTimeout(() => abortCtrl.abort(), IDLE_MS);
          const touchIdle = () => {
            clearTimeout(abortTimer);
            abortTimer = setTimeout(() => abortCtrl.abort(), IDLE_MS);
          };
          const fetchRes = await fetch(investigateUrl, {
            method: 'POST',
            headers: buildInvestigateFetchHeaders(teamId),
            body: JSON.stringify(investigatePayload),
            credentials: 'include',
            signal: abortCtrl.signal,
          });
          const ct = (fetchRes.headers.get('Content-Type') || '').toLowerCase();

          if (fetchRes.ok && ct.includes('text/event-stream')) {
            const streamKey = `inv_stream_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
            const initialStream = {
              thinking: true,
              thinkingLabel: 'Thinking...',
              tools: [],
              toolResults: [],
              reasoning: [],
              steps: [],
              plan: null,
              resultKind: null,
              body: '',
              streaming: true,
              calls: [],
              startedAt: Date.now(),
            };
            setMessages((m) => [
              ...m,
              {
                id: streamKey,
                _clientKey: streamKey,
                role: 'assistant',
                investigateStream: initialStream,
              },
            ]);

            const patchStreamRow = (fn) => {
              setMessages((m) =>
                m.map((row) => {
                  if (row._clientKey !== streamKey || row.role !== 'assistant') return row;
                  const prev = row.investigateStream || initialStream;
                  return { ...row, investigateStream: fn(prev) };
                })
              );
            };

            let terminalReceived = false;
            let streamFailed = null;
            streamingRef.current = true;
            try {
              await consumeInvestigateSse(fetchRes, (eventName, data) => {
                touchIdle(); // any event: heartbeats included: proves the stream is alive
                const ev = (eventName || 'message').trim();
                if (ev === 'heartbeat') return false;
                if (ev === 'run') {
                  // The run this stream follows: what Stop cancels.
                  const runId = typeof data?.ask_run_id === 'string' ? data.ask_run_id : null;
                  if (runId) patchStreamRow((prev) => ({ ...prev, askRunId: runId, runMode: data?.mode || null }));
                  return false;
                }
                if (ev === 'dashboard') {
                  // Ask created or changed a dashboard (TRO-469). The shell
                  // opens it beside the chat; an open dashboard page reloads.
                  if (data && typeof data === 'object' && typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('trodo:dashboard', { detail: { ...data, session_id: sid } }));
                  }
                  return false;
                }
                if (ev === 'thinking') {
                  const text =
                    typeof data?.message === 'string' && data.message.trim()
                      ? data.message.trim()
                      : 'Thinking...';
                  patchStreamRow((prev) => ({ ...prev, thinking: true, thinkingLabel: text }));
                  return false;
                }
                if (ev === 'step') {
                  // v2 structured step log: upsert by (phase, id) so a node's
                  // pending → running → ok transitions update one row in place.
                  if (!data || typeof data !== 'object' || !data.phase) return false;
                  patchStreamRow((prev) => {
                    const steps = [...(prev.steps || [])];
                    const key = `${data.phase}:${data.id || ''}`;
                    const idx = steps.findIndex((s) => `${s.phase}:${s.id || ''}` === key);
                    if (idx >= 0) steps[idx] = { ...steps[idx], ...data };
                    else steps.push({ ...data });
                    return {
                      ...prev,
                      thinking: true,
                      steps,
                      ...(data.phase === 'plan' && data.plan ? { plan: data.plan } : {}),
                    };
                  });
                  return false;
                }
                if (ev === 'calling_tool') {
                  const tool =
                    typeof data?.tool === 'string'
                      ? data.tool
                      : data?.tool != null
                        ? String(data.tool)
                        : '';
                  const name = tool.trim();
                  if (!name) return false;
                  patchStreamRow((prev) => {
                    const tools = [...(prev.tools || [])];
                    if (!tools.includes(name)) tools.push(name);
                    return { ...prev, tools, calls: [...(prev.calls || []), callFrom(data)] };
                  });
                  return false;
                }
                if (ev === 'tool_result') {
                  // Per-tool result with optional summary + chat_render widget.
                  // Append to toolResults so we can render an inline card the
                  // moment its tool finishes (progressive widget mounting).
                  const entry = {
                    cycle: typeof data?.cycle === 'number' ? data.cycle : null,
                    tool: typeof data?.tool === 'string' ? data.tool : '',
                    // Keep this list identical to the replay builder above.
                    name: typeof data?.name === 'string' ? data.name : '',
                    detail: typeof data?.detail === 'string' ? data.detail : '',
                    rows: typeof data?.rows === 'number' ? data.rows : null,
                    dataset: typeof data?.dataset === 'string' ? data.dataset : '',
                    wave: typeof data?.wave === 'number' ? data.wave : null,
                    status: typeof data?.status === 'string' ? data.status : 'unknown',
                    summary: typeof data?.summary === 'string' ? data.summary : '',
                    error: typeof data?.error === 'string' ? data.error : '',
                    chat_render:
                      data?.chat_render && typeof data.chat_render === 'object'
                        ? data.chat_render
                        : null,
                  };
                  patchStreamRow((prev) => ({
                    ...prev,
                    toolResults: [...(prev.toolResults || []), entry],
                  }));
                  return false;
                }
                if (ev === 'reasoning') {
                  const reason = typeof data?.reason === 'string' ? data.reason.trim() : '';
                  if (!reason) return false;
                  const cycle = typeof data?.cycle === 'number' ? data.cycle : null;
                  const confidence =
                    typeof data?.confidence === 'number' ? data.confidence : null;
                  const sufficient = !!data?.sufficient;
                  patchStreamRow((prev) => ({
                    ...prev,
                    reasoning: [
                      ...(prev.reasoning || []),
                      { cycle, reason, confidence, sufficient },
                    ],
                  }));
                  return false;
                }
                if (ev === 'delta') {
                  // Token-level streaming: append to body. The thinking
                  // spinner switches off the moment we start receiving deltas.
                  const text = typeof data?.text === 'string' ? data.text : '';
                  if (!text) return false;
                  patchStreamRow((prev) => ({
                    ...prev,
                    thinking: false,
                    resultKind: 'answer',
                    body: (prev.body || '') + text,
                  }));
                  return false;
                }
                if (ev === 'error') {
                  const errMsg =
                    typeof data?.message === 'string' && data.message.trim()
                      ? data.message.trim()
                      : 'Investigation failed.';
                  terminalReceived = true;
                  // Shown once, in the thread. Also raising the page banner
                  // printed the same sentence twice.
                  patchStreamRow((prev) => ({
                    ...prev,
                    thinking: false,
                    streaming: false,
                    endedAt: Date.now(),
                    resultKind: 'ended',
                    endReason: data?.reason === 'cancelled' ? 'cancelled' : 'failed',
                    body: errMsg,
                  }));
                  return true;
                }
                if (ev === 'answer') {
                  const finalAnswer =
                    typeof data?.finalAnswer === 'string' ? data.finalAnswer : '';
                  // The Answer Document: typed blocks the renderer walks.
                  // The markdown body stays as the copy/search projection.
                  const answerDoc = data?.answer && typeof data.answer === 'object' ? data.answer : null;
                  terminalReceived = true;
                  patchStreamRow((prev) => ({
                    ...prev,
                    thinking: false,
                    streaming: false,
                    endedAt: Date.now(),
                    // Out of credit arrives as an answer frame marked `ended`.
                    resultKind: data?.ended ? 'ended' : 'answer',
                    ...(data?.ended ? { endReason: data.ended } : {}),
                    // The final 'answer' frame carries the linkified +
                    // sanitised full text. Trust it over the accumulated
                    // raw deltas so entity links + sanitiser fixes apply.
                    body: finalAnswer || prev.body || '',
                    // Arrived just now: the thread types it out (AnswerRenderer reveal).
                    ...(answerDoc ? { answer: answerDoc, revealAt: (revealRef.current = Date.now()) } : {}),
                  }));
                  if (userId) invalidateChatCache(userId, teamId, sid);
                  return true;
                }
                if (ev === 'clarify') {
                  const q =
                    typeof data?.question === 'string'
                      ? data.question
                      : data?.question != null
                        ? String(data.question)
                        : '';
                  const options = Array.isArray(data?.options) ? data.options : null;
                  terminalReceived = true;
                  patchStreamRow((prev) => ({
                    ...prev,
                    thinking: false,
                    streaming: false,
                    endedAt: Date.now(),
                    resultKind: 'clarify',
                    body: q,
                    ...(options ? { clarifyOptions: options } : {}),
                    // Structured questions + the id that lets an answer
                    // resume the OPEN run instead of posting a new message.
                    ...(Array.isArray(data?.questions) && data.questions.length ? { clarifyQuestions: data.questions } : {}),
                    ...(data?.clarificationId ? { clarificationId: String(data.clarificationId) } : {}),
                  }));
                  if (userId) invalidateChatCache(userId, teamId, sid);
                  return true;
                }
                return false;
              });
            } catch (streamErr) {
              // THE STREAM DYING IS NOT A FAILED SEND.
              //
              // There was no catch here, so a reader that threw — the server
              // restarting, a laptop sleeping, wifi dropping — skipped the
              // whole recovery block below and escaped to the outer handler.
              // What the user saw was a row spinning on "Working…" for ever,
              // their question gone from the thread, and a generic banner at
              // the bottom of the page. The run itself usually finished and
              // persisted server-side; nothing ever went back for it.
              //
              // Swallowed on purpose: `terminalReceived` is still false, so
              // the block below settles the row and reloads the thread, which
              // is exactly the right response whether the stream ended cleanly
              // or was cut.
              streamFailed = streamErr;
            } finally {
              streamingRef.current = false;
              clearTimeout(abortTimer);
            }

            let lostMsg = '';
            if (!terminalReceived) {
              // Why it ended matters when reading a report of this later: a
              // cut connection and a clean end that simply never sent a
              // terminal event are different bugs.
              if (streamFailed) {
                console.warn('[chat] stream ended early:', streamFailed?.message || streamFailed);
              }
              // Deep research outlives its stream by design — the run is on a
              // worker, and this page was only ever a viewer. Reloading the
              // thread below picks up whatever the run has persisted since.
              lostMsg = isDeepRequest
                ? 'The live connection dropped, but the research is still running in the background: its results will appear in this chat when it finishes.'
                : 'Connection lost before answer arrived. Please try again.';
              patchStreamRow((prev) => ({
                ...prev,
                thinking: false,
                // Without this the row keeps its live styling and goes on
                // saying "Working…" — the spinner that never resolved.
                streaming: false,
                endedAt: Date.now(),
                resultKind: isDeepRequest ? 'answer' : 'error',
                body: lostMsg,
              }));
            }

            // RELOAD FIRST, THEN JUDGE. The run usually outlives the stream —
            // it is finishing on the server while the browser is being told
            // nothing — so the persisted answer may already be there. Reload
            // before deciding whether anything was actually lost, and never
            // let a failed reload throw: it would land in the outer handler,
            // overwrite the explanation with a generic connection error and
            // mark a question that was sent perfectly well as "failed to
            // send".
            const reloaded = await loadMessagesForSession(sid, null, { silent: true })
              .then(() => true)
              .catch(() => false);

            if (!terminalReceived && !isDeepRequest) {
              // Only now, when the thread has had its chance to produce one.
              const rows = reloaded ? (loadedRef.current || []) : [];
              const answered = rows.length > 0 && rows[rows.length - 1]?.role === 'assistant';
              // setError LAST: loadMessagesForSession clears the error on
              // entry, so anything set before the reload is wiped by it.
              if (!answered) setError(lostMsg);
            }

            listData = await listChatSessions(teamId).catch(() => listData);
            setSessions(listData?.sessions || []);
          } else if (fetchRes.ok) {
            const invRes = await fetchRes.json();
            dashboardExecution = invRes.dashboardExecution || null;
            if (userId) invalidateChatCache(userId, teamId, sid);
            await loadMessagesForSession(sid, null, { silent: true });
            listData = await listChatSessions(teamId);
            setSessions(listData.sessions || []);
          } else {
            let errMsg = 'Investigation failed';
            try {
              const errBody = await fetchRes.json();
              errMsg = errBody.message || errBody.detail || errMsg;
            } catch {
              errMsg = fetchRes.statusText || errMsg;
            }
            const e = new Error(errMsg);
            e.response = { status: fetchRes.status, data: { message: errMsg } };
            throw e;
          }
        } catch (planErr) {
          const st = planErr.response?.status;
          dashboardExecution =
            planErr.response?.data?.dashboardExecution || null;
          if (st === 503) {
            /* Planner/orchestrator not configured — skip silently; may still have dashboardExecution */
          } else if (!st || st >= 500) {
            setError(
              planErr.response?.data?.message || planErr.message || 'Investigation failed'
            );
          }
        }

        if (payloadAttachments.length > 0 && !dashboardExecution) {
          try {
            await postChatContext({
              message: displayText,
              attachments: payloadAttachments,
            });
            if (userId) invalidateChatCache(userId, teamId, sid);
            await loadMessagesForSession(sid);
            listData = await listChatSessions(teamId);
            setSessions(listData.sessions || []);
          } catch (ctxErr) {
            const ctxMsg =
              ctxErr.response?.data?.message || ctxErr.message || 'Failed to load dashboard context';
            setError(ctxMsg);
            if (userId) invalidateChatCache(userId, teamId, sid);
            await loadMessagesForSession(sid);
          }
        }
      } catch (err) {
        const msg = err.response?.data?.message || err.message || 'Send failed';
        setError(msg);
        setMessages((m) =>
          m.map((x) =>
            x._clientKey === clientKey ? { ...x, pending: false, failed: true } : x
          )
        );
      } finally {
        setSending(false);
      }
    },
    [teamId, activeSessionId, sending, userId, setSearchParams, loadMessagesForSession]
  );

  /** Placeholder for future SSE / LLM completion */
  const handleAssistantResponse = useCallback(async () => {}, []);

  return {
    userId,
    sessions,
    messages,
    activeSessionId,
    sessionsLoading,
    messagesLoading,
    sending,
    awaitingReply,
    error,
    setError,
    messagesHasMore,
    loadOlderMessages,
    loadMessagesForSession,
    selectSession,
    newChat,
    sendMessage,
    handleAssistantResponse,
    homeBootstrapMessage,
    setSearchParams,
  };
}
